import { BaseService } from './base.service';
import { CustomResponseEntity } from '../types/common';
import { InspectionProgramItemDto } from '../types/dtos/inspection';
import { api } from '../client/axiosClient';
import { ChallengeTableInspectionsProgramsVersionsItem } from '../types/dtos/challenge/ChallengeDto';

/**
 * InspectionProgramItemService handles CRUD operations for inspection program items.
 *
 * URL Pattern: /api/v1/inspections/{inspectionId}/programs/{programId}/items
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * @deprecated organizationId constructor parameter is deprecated and no longer used.
 */
export class InspectionProgramItemService extends BaseService<InspectionProgramItemDto> {
  private basePath: string;

  /**
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param programVersionId - The program version ID (programId)
   * @param inspectionId - The inspection ID for nested resources
   */
  constructor(_organizationId: string, programVersionId: string, inspectionId: string) {
    super(`/api/v1/inspections/${inspectionId}/programs/${programVersionId}/items`);
    this.basePath = `/api/v1/inspections/${inspectionId}/programs/${programVersionId}/items`;
  }

  findByType(type: string): Promise<CustomResponseEntity<InspectionProgramItemDto[]>> {
    return api.get(`${this.basePath}/type/${type}`);
  }

  updateStatus(id: string, status: string): Promise<CustomResponseEntity<InspectionProgramItemDto>> {
    return api.put(`${this.basePath}/${id}/status`, { status });
  }

  updateValue(id: string, value: string): Promise<CustomResponseEntity<InspectionProgramItemDto>> {
    return api.put(`${this.basePath}/${id}/value`, { value });
  }

  addComment(id: string, comment: string): Promise<CustomResponseEntity<InspectionProgramItemDto>> {
    return api.put(`${this.basePath}/${id}/comment`, { comment });
  }

  addEvidence(id: string, evidenceUrl: string): Promise<CustomResponseEntity<InspectionProgramItemDto>> {
    return api.put(`${this.basePath}/${id}/evidence`, { evidenceUrl });
  }

  updateItemDetails(
    itemId: string,
    payload: Partial<Pick<ChallengeTableInspectionsProgramsVersionsItem, 'isActive' | 'isApproved' | 'comments' | 'value' | 'createdAt' | 'updatedAt' | 'startDate' | 'endDate'>>
  ): Promise<CustomResponseEntity<ChallengeTableInspectionsProgramsVersionsItem>> {
    return api.put(`${this.basePath}/${itemId}`, payload);
  }

  /**
   * Upload a file for a specific inspection program item.
   * @param itemId The ID of the item
   * @param file The file to upload
   * @param description Optional description
   * @param onUploadProgress Optional progress callback
   */
  async uploadFile(
    itemId: string,
    file: File,
    description?: string,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('fileType', this.inferFileType(file));
    if (description) formData.append('description', description);

    const response: any = await api.post(
      `${this.basePath}/${itemId}/files/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
        timeout: 120000, // 2 minutes for file uploads
      }
    );
    return response.data;
  }

  /**
   * Infer file type (audio, video, image, document) from file object
   */
  private inferFileType(file: File): string {
    const mime = file.type;
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    if (
      mime === 'application/pdf' ||
      mime.startsWith('application/msword') ||
      mime.startsWith('application/vnd') ||
      mime.startsWith('text/')
    ) return 'document';
    return 'document';
  }
}

/**
 * Create an InspectionProgramItemService instance.
 * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
 */
export const createInspectionProgramItemService = (
  _organizationId: string,
  programVersionId: string,
  inspectionId: string
) => new InspectionProgramItemService(_organizationId, programVersionId, inspectionId); 